import * as Popover from '@radix-ui/react-popover';
import { Info } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

/** Hover/focus help that can also be pinned with a tap. Portalled to avoid card clipping. */
export const HelpTooltip = ({
  label,
  children,
  icon,
  className,
  contentRole = 'tooltip',
}: {
  label: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  contentRole?: 'tooltip' | 'dialog';
}) => {
  const [open, setOpen] = React.useState(false);
  const pinned = React.useRef(false);
  const trigger = React.useRef<HTMLButtonElement>(null);
  const content = React.useRef<HTMLDivElement>(null);
  const restoringFocus = React.useRef(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const id = React.useId();
  const cancelClose = () => clearTimeout(timer.current);
  const reveal = () => {
    cancelClose();
    setOpen(true);
  };
  const scheduleClose = () => {
    cancelClose();
    timer.current = setTimeout(() => {
      if (
        !pinned.current &&
        document.activeElement !== trigger.current &&
        !content.current?.contains(document.activeElement)
      ) {
        setOpen(false);
      }
    }, 150);
  };
  React.useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        cancelClose();
        pinned.current = false;
        setOpen(next);
      }}
    >
      <Popover.Anchor asChild>
        <button
          ref={trigger}
          type="button"
          aria-label={label}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          aria-describedby={open && contentRole === 'tooltip' ? id : undefined}
          aria-haspopup={contentRole === 'dialog' ? 'dialog' : undefined}
          className={cn(
            'inline-flex size-6 shrink-0 items-center justify-center rounded text-ink-muted hover:bg-surface-muted hover:text-ink focus-visible:text-ink',
            className,
          )}
          onPointerEnter={(event) => {
            if (event.pointerType === 'mouse') {
              reveal();
            }
          }}
          onPointerLeave={scheduleClose}
          onFocus={() => {
            if (!restoringFocus.current) {
              reveal();
            }
          }}
          onBlur={scheduleClose}
          onClick={() => {
            cancelClose();
            pinned.current = !pinned.current;
            setOpen(pinned.current);
            if (pinned.current && contentRole === 'dialog') {
              requestAnimationFrame(() => content.current?.focus());
            }
          }}
        >
          {icon ?? <Info aria-hidden="true" className="size-3.5" />}
        </button>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          ref={content}
          id={id}
          role={contentRole}
          tabIndex={contentRole === 'dialog' ? -1 : undefined}
          aria-label={label}
          side="top"
          sideOffset={6}
          collisionPadding={12}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={() => {
            if (contentRole === 'dialog') {
              restoringFocus.current = true;
              trigger.current?.focus();
              restoringFocus.current = false;
            }
          }}
          onInteractOutside={(event) => {
            // The anchor handles its own tap-to-toggle behavior.
            if (trigger.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          onFocusCapture={cancelClose}
          onBlurCapture={scheduleClose}
          className="z-50 max-h-[var(--radix-popover-content-available-height)] w-72 max-w-[calc(100vw-24px)] overflow-y-auto rounded-lg border border-border-strong bg-surface p-3 text-xs leading-relaxed text-ink"
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
